import React, { useMemo } from 'react';
import { FormControl, FormLabel, Select, MenuItem } from '@mui/material';
import { getLanguage, getPolyglot } from '../../../../../i18n';
import { ErrorText } from '../../../../../theme/components/Forms';
import { FormikProps } from 'formik';

const SUPPORTED_ZONETIMES = new Set([
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
]);

function getTimeZoneLabel(timeZone: string, date = new Date()): string {
  const locale = getLanguage();
  const offset = new Intl.DateTimeFormat(locale, {
    timeZone,
    timeZoneName: 'longOffset',
  })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName')?.value;

  const name = new Intl.DateTimeFormat(locale, {
    timeZone,
    timeZoneName: 'longGeneric',
  })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName')?.value;

  const city = timeZone.split('/').pop()?.replaceAll('_', ' ');

  return `(${offset}) ${city} - ${name}`;
}

interface TimeZoneField {
  timezone: string;
}

interface TimeZoneSelectionFieldProps<T extends TimeZoneField> {
  formik: FormikProps<T>;
}

const TimeZoneSelectionField = <T extends TimeZoneField>({
  formik,
}: TimeZoneSelectionFieldProps<T>) => {
  const polyglot = getPolyglot();

  const allTimeZones = useMemo(
    () =>
      Intl.supportedValuesOf('timeZone')
        .slice()
        .filter((tz) => SUPPORTED_ZONETIMES.has(tz))
        .sort((a, b) => a.localeCompare(b)),
    []
  );

  return (
    <FormControl
      fullWidth
      sx={{
        gap: '0.5rem',
        display: 'flex',
        justifyContent: 'flex-start',
      }}
      required
      error={formik.touched.timezone && Boolean(formik.errors.timezone)}
    >
      <FormLabel htmlFor="timezone" required>
        {polyglot.t('freshwatch.merchandiser.add.timezone')}
      </FormLabel>
      <Select
        id="timezone"
        name="timezone"
        value={formik.values.timezone}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        displayEmpty
      >
        <MenuItem value="" disabled>
          {polyglot.t('freshwatch.merchandiser.add.select_timezone')}
        </MenuItem>
        {allTimeZones.map((timezone) => (
          <MenuItem key={timezone} value={timezone}>
            {getTimeZoneLabel(timezone)}
          </MenuItem>
        ))}
      </Select>
      {formik.touched.timezone && formik.errors.timezone && (
        <ErrorText>{polyglot.t(formik.errors.timezone as string)}</ErrorText>
      )}
    </FormControl>
  );
};

export default TimeZoneSelectionField;
